import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { fromError } from "zod-validation-error";

export const useCreateUserGroupMutation = (
    setOpen?: (open: boolean) => void,
    setLoading?: (loading: boolean) => void,
    setError?: (error: string) => void
) => {
    const utils = trpc.useUtils();
    return trpc.userGroups.create.useMutation({
        onMutate: async () => {
            setLoading?.(true);
            setError?.("");
        },
        onSuccess: async () => {
            utils.userGroups.findMany.invalidate();
            setOpen?.(false);
            toast.success("User group created successfully");
        },
        onError: async (error: any) => {
            const errorMessage = fromError(error).toString();
            toast.error(errorMessage);
            setError?.(errorMessage);
        },
        onSettled: async () => {
            setLoading?.(false);
        },
    });
};

export const useUpdateUserGroupMutation = (
    userGroupId: string,
    setOpen?: (open: boolean) => void,
    setLoading?: (loading: boolean) => void,
    setError?: (error: string) => void
) => {
    const utils = trpc.useUtils();
    return trpc.userGroups.update.useMutation({
        onMutate: async () => {
            setLoading?.(true);
            setError?.("");
        },
        onSuccess: async () => {
            utils.userGroups.findMany.invalidate();
            utils.userGroups.findOne.invalidate(userGroupId);
            utils.userGroups.findOneWithConnections.invalidate(userGroupId);
            setOpen?.(false);
            toast.success("User group updated successfully");
        },
        onError: async (error: any) => {
            const errorMessage = fromError(error).toString();
            toast.error(errorMessage);
            setError?.(errorMessage);
        },
        onSettled: async () => {
            setLoading?.(false);
        },
    });
};

export const useDeleteUserGroupMutation = () => {
    const utils = trpc.useUtils();
    return trpc.userGroups.remove.useMutation({
        onSuccess: async () => {
            utils.userGroups.findMany.invalidate();
            toast.success("User group(s) deleted successfully");
        },
        onError: async (error: any) => {
            const errorMessage = fromError(error).toString();
            toast.error(errorMessage);
        },
    });
};

export const useAssignAgentConnectionsMutation = () => {
    const utils = trpc.useUtils();
    return trpc.userGroups.assignAgentConnections.useMutation({
        onSuccess: async (_, variables) => {
            utils.userGroups.findOneWithConnections.invalidate(variables.userGroupId);
            utils.userGroups.getAgentConnectionsForGroup.invalidate(variables.userGroupId);
            toast.success("Agent connections assigned successfully");
        },
        onError: async (error: any) => {
            const errorMessage = fromError(error).toString();
            toast.error(errorMessage);
        },
    });
};

export const useRemoveAgentConnectionsMutation = () => {
    const utils = trpc.useUtils();
    return trpc.userGroups.removeAgentConnections.useMutation({
        onSuccess: async (_, variables) => {
            utils.userGroups.findOneWithConnections.invalidate(variables.userGroupId);
            utils.userGroups.getAgentConnectionsForGroup.invalidate(variables.userGroupId);
            toast.success("Agent connections removed successfully");
        },
        onError: async (error: any) => {
            const errorMessage = fromError(error).toString();
            toast.error(errorMessage);
        },
    });
};

export const useAssignUsersMutation = () => {
    const utils = trpc.useUtils();
    return trpc.userGroups.assignUsers.useMutation({
        onSuccess: async (_, variables) => {
            utils.userGroups.findOneWithUsers.invalidate(variables.userGroupId);
            utils.userGroups.getUsersForGroup.invalidate(variables.userGroupId);
            toast.success("Users assigned successfully");
        },
        onError: async (error: any) => {
            const errorMessage = fromError(error).toString();
            toast.error(errorMessage);
        },
    });
};

export const useRemoveUsersMutation = () => {
    const utils = trpc.useUtils();
    return trpc.userGroups.removeUsers.useMutation({
        onSuccess: async (_, variables) => {
            utils.userGroups.findOneWithUsers.invalidate(variables.userGroupId);
            utils.userGroups.getUsersForGroup.invalidate(variables.userGroupId);
            toast.success("Users removed successfully");
        },
        onError: async (error: any) => {
            const errorMessage = fromError(error).toString();
            toast.error(errorMessage);
        },
    });
};

// IDP Group Import mutation
export const useImportGroupsMutation = (
    setOpen?: (open: boolean) => void,
    setLoading?: (loading: boolean) => void,
    setError?: (error: string) => void
) => {
    const utils = trpc.useUtils();
    return trpc.userGroups.importGroups.useMutation({
        onMutate: async () => {
            setLoading?.(true);
            setError?.("");
        },
        onSuccess: async (result) => {
            utils.userGroups.findMany.invalidate();
            setOpen?.(false);
            
            if (result.failedImports > 0) {
                toast.success(
                    `Import completed: ${result.successfulImports} groups imported successfully, ${result.failedImports} failed`
                );
            } else {
                toast.success(`Successfully imported ${result.successfulImports} groups from IDP`);
            }
        },
        onError: async (error: any) => {
            const errorMessage = fromError(error).toString();
            toast.error(errorMessage);
            setError?.(errorMessage);
        },
        onSettled: async () => {
            setLoading?.(false);
        },
    });
};

export const useSyncAllGroupsNowMutation = () => {
    const utils = trpc.useUtils();
    return trpc.userGroups.syncAllGroupsNow.useMutation({
        onMutate: async () => {
            toast.info("Starting group sync from IDP...");
        },
        onSuccess: async () => {
            utils.userGroups.findMany.invalidate();
            toast.success("Successfully synced all groups from IDP");
        },
        onError: async (error: any) => {
            const errorMessage = fromError(error).toString();
            toast.error(`Group sync failed: ${errorMessage}`);
        },
    });
};
